import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSportsLang } from '../contexts/SportsLangContext';
import { VUKZ, VUKZ_GRADIENT } from '../theme/vukzTheme';
import PageHeader from '../components/layout/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import {
  getAthleteRankingSummary,
  getVukzRankings,
  getCoachRankingBadges,
  getCoachAthletes,
  parseEventLabel,
  buildEventKey,
} from '../services/vukzRankingsService';
import { getEvents } from '../services/swimmingService';
import {
  Trophy, Timer, Medal, TrendingUp, ChevronDown,
  User, Users, Building2, Filter, BarChart3, Target,
  Calendar, Award, Waves, ArrowUpRight, ArrowDownRight, Minus,
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════
   CoachPerformancePage — Performance dos Meus Atletas (Treinador)
   Route: /coach/performance
   Widgets:
     1. Ranking dos Meus Atletas — all athletes' best events + ranks
     2. Meus Atletas vs Ranking — selected event, athletes vs top
     3. Visão por Atleta — drill-down into one athlete
   ═══════════════════════════════════════════════════════════════ */

const LABELS = {
  pt: {
    title: 'Performance dos Meus Atletas',
    subtitle: (n) => `Rankings e evolução do seu elenco · ${n} atleta${n !== 1 ? 's' : ''}`,
    badge: 'TREINADOR',
    genderM: '♂ Masc',
    genderF: '♀ Fem',
    loading: 'Carregando...',
    w1title: 'Meus Atletas na Prova',
    w1sub: (label, yr) => `${label} · ${yr}`,
    w1subEmpty: 'Selecione uma prova',
    eventPh: 'Selecione a prova',
    w1empty1: 'Selecione uma prova para ver seus atletas',
    w1empty2: 'Nenhum dos seus atletas competiu nesta prova',
    cols1: ['Atleta', 'Melhor Tempo', 'Rank VUKZ', 'Rank Brasil'],
    myBadge: 'MEU',
    w2title: 'Meus Atletas vs Ranking VUKZ',
    w2sub: (label, yr) => `${label} · Top 50 VUKZ ${yr}`,
    w2subEmpty: 'Selecione uma prova acima',
    w2empty1: 'Selecione uma prova para comparar',
    w2empty2: 'Nenhum dado de ranking para esta prova',
    cols2: ['#', 'Atleta', 'País', 'Categoria', 'Tempo'],
    w3title: 'Visão por Atleta',
    w3sub: (yr) => `Todas as provas · ${yr}`,
    w3subEmpty: 'Selecione um atleta',
    athletePh: 'Selecione o atleta',
    w3empty1: 'Selecione um atleta para ver o resumo',
    w3empty2: (yr) => `Nenhum resultado encontrado em ${yr}`,
    cols3: ['Prova', 'Melhor Tempo', 'VUKZ', 'Brasil', 'EUA', 'Austrália'],
  },
  en: {
    title: "My Athletes' Performance",
    subtitle: (n) => `Rankings and progress of your squad · ${n} athlete${n !== 1 ? 's' : ''}`,
    badge: 'COACH',
    genderM: '♂ Male',
    genderF: '♀ Female',
    loading: 'Loading...',
    w1title: 'My Athletes in Event',
    w1sub: (label, yr) => `${label} · ${yr}`,
    w1subEmpty: 'Select an event',
    eventPh: 'Select event',
    w1empty1: 'Select an event to see your athletes',
    w1empty2: 'None of your athletes competed in this event',
    cols1: ['Athlete', 'Best Time', 'VUKZ Rank', 'Brazil Rank'],
    myBadge: 'MINE',
    w2title: 'My Athletes vs VUKZ Ranking',
    w2sub: (label, yr) => `${label} · Top 50 VUKZ ${yr}`,
    w2subEmpty: 'Select an event above',
    w2empty1: 'Select an event to compare',
    w2empty2: 'No ranking data for this event',
    cols2: ['#', 'Athlete', 'Country', 'Category', 'Time'],
    w3title: 'Athlete View',
    w3sub: (yr) => `All events · ${yr}`,
    w3subEmpty: 'Select an athlete',
    athletePh: 'Select athlete',
    w3empty1: 'Select an athlete to see summary',
    w3empty2: (yr) => `No results found in ${yr}`,
    cols3: ['Event', 'Best Time', 'VUKZ', 'Brazil', 'USA', 'Australia'],
  },
};

const CURRENT_YEAR = new Date().getFullYear();
const AVAILABLE_YEARS = [2025, 2024, 2023];

// ── Helpers ──────────────────────────────────────────────────

const normalizeStroke = (s) => {
  if (!s) return '';
  const upper = s.toUpperCase().trim();
  if (upper === 'NADO LIVRE' || upper === 'LIVRE') return 'Livre';
  if (upper === 'COSTAS') return 'Costas';
  if (upper === 'PEITO') return 'Peito';
  if (upper === 'BORBOLETA') return 'Borboleta';
  if (upper === 'MEDLEY') return 'Medley';
  return s;
};

const deduplicateEvents = (events) => {
  const map = new Map();
  for (const e of events) {
    const key = `${e.distance_meters}-${normalizeStroke(e.stroke)}-${e.gender}`;
    if (!map.has(key)) {
      map.set(key, {
        ...e,
        displayName: `${e.distance_meters}m ${normalizeStroke(e.stroke)}`,
        normalizedStroke: normalizeStroke(e.stroke),
      });
    }
  }
  return Array.from(map.values()).sort(
    (a, b) => a.distance_meters - b.distance_meters || a.normalizedStroke.localeCompare(b.normalizedStroke)
  );
};

// ── Reusable Components ──────────────────────────────────────

const SelectPill = ({ value, onChange, options, icon: Icon, placeholder }) => (
  <div className="relative">
    <div className="flex items-center gap-1.5 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
      style={{ color: VUKZ.textLight }}>
      {Icon && <Icon size={13} />}
    </div>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="appearance-none pl-8 pr-8 py-2 rounded-xl text-xs font-bold border bg-white cursor-pointer hover:border-gray-300 focus:outline-none focus:ring-2 transition-all"
      style={{ borderColor: VUKZ.border, color: VUKZ.text }}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
      style={{ color: VUKZ.textLight }} />
  </div>
);

const GenderToggle = ({ value, onChange }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  return (
    <div className="flex rounded-xl border overflow-hidden" style={{ borderColor: VUKZ.border }}>
      {[
        { key: 'M', label: t.genderM },
        { key: 'F', label: t.genderF },
      ].map((g) => (
        <button
          key={g.key}
          onClick={() => onChange(g.key)}
          className="px-4 py-2 text-xs font-bold transition-all"
          style={{
            backgroundColor: value === g.key ? VUKZ.primary : 'white',
            color: value === g.key ? 'white' : VUKZ.textLight,
          }}
        >
          {g.label}
        </button>
      ))}
    </div>
  );
};

const WidgetCard = ({ icon: Icon, title, subtitle, filters, children, className = '' }) => (
  <div className={`bg-white rounded-2xl border overflow-hidden ${className}`} style={{ borderColor: VUKZ.border }}>
    <div className="px-5 pt-5 pb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: VUKZ_GRADIENT }}>
            <Icon size={18} color="white" />
          </div>
          <div>
            <h2 className="text-sm font-extrabold" style={{ color: VUKZ.text }}>{title}</h2>
            {subtitle && (
              <p className="text-[10px]" style={{ color: VUKZ.textLight }}>{subtitle}</p>
            )}
          </div>
        </div>
      </div>
      {filters && (
        <div className="flex flex-wrap items-center gap-2">{filters}</div>
      )}
    </div>
    <div className="px-5 pb-5">{children}</div>
  </div>
);

const SkeletonRows = ({ count = 4 }) => (
  <div className="space-y-2">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="h-12 rounded-xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
    ))}
  </div>
);

const EmptyState = ({ icon: Icon, message }) => (
  <div className="text-center py-10">
    <div className="w-12 h-12 rounded-2xl mx-auto mb-3 flex items-center justify-center"
      style={{ backgroundColor: VUKZ.primaryLight }}>
      <Icon size={20} style={{ color: VUKZ.textLight }} />
    </div>
    <p className="text-xs font-medium" style={{ color: VUKZ.textLight }}>{message}</p>
  </div>
);

const RankBadge = ({ rank, scope }) => {
  if (!rank) return <span className="text-[10px]" style={{ color: VUKZ.textLight }}>—</span>;
  const colors = {
    vukz: { bg: VUKZ.primary },
    br: { bg: '#16a34a' },
    usa: { bg: '#2563eb' },
    aus: { bg: '#f59e0b' },
  };
  const c = colors[scope] || colors.vukz;
  return (
    <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-md inline-block"
      style={{ backgroundColor: `${c.bg}18`, color: c.bg }}>
      #{rank}
    </span>
  );
};

// ── Main Page ────────────────────────────────────────────────

const CoachPerformancePage = () => {
  const { user } = useAuth();
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;

  // Global filters
  const [year, setYear] = useState(String(CURRENT_YEAR));
  const [gender, setGender] = useState('M');

  // Athletes
  const [athletes, setAthletes] = useState([]);
  const [athletesLoading, setAthletesLoading] = useState(true);

  // Events
  const [allEvents, setAllEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  // Widget 1: Badges overview
  const [selectedEventKey, setSelectedEventKey] = useState('');
  const [badges, setBadges] = useState([]);
  const [badgesLoading, setBadgesLoading] = useState(false);

  // Widget 2: Full ranking comparison
  const [comparisonData, setComparisonData] = useState([]);
  const [comparisonLoading, setComparisonLoading] = useState(false);

  // Widget 3: Athlete drill-down
  const [selectedAthleteId, setSelectedAthleteId] = useState('');
  const [athleteSummary, setAthleteSummary] = useState([]);
  const [athleteSummaryLoading, setAthleteSummaryLoading] = useState(false);

  // ── Load athletes + events on mount ──
  useEffect(() => {
    const load = async () => {
      setAthletesLoading(true);
      setEventsLoading(true);
      try {
        const [athletesRes, eventsRes] = await Promise.all([
          getCoachAthletes(),
          getEvents({ is_relay: 0 }),
        ]);
        const ath = athletesRes?.athletes || athletesRes?.items || [];
        setAthletes(ath);
        if (ath.length > 0 && !selectedAthleteId) {
          setSelectedAthleteId(String(ath[0].athlete_user_id || ath[0].id));
        }

        const raw = eventsRes?.events || eventsRes?.items || eventsRes || [];
        setAllEvents(raw);
      } catch (err) {
        console.error('Error loading athletes/events:', err);
      } finally {
        setAthletesLoading(false);
        setEventsLoading(false);
      }
    };
    load();
  }, []);

  // ── Filtered events by gender ──
  const filteredEvents = deduplicateEvents(
    allEvents.filter((e) => e.gender === gender)
  );

  // Auto-select first event when gender changes
  useEffect(() => {
    if (filteredEvents.length > 0 && !selectedEventKey) {
      const first = filteredEvents[0];
      const key = buildEventKey(first.distance_meters, first.normalizedStroke, gender);
      setSelectedEventKey(key);
    }
  }, [filteredEvents, selectedEventKey, gender]);

  // Reset event on gender change
  const handleGenderChange = (g) => {
    setGender(g);
    setSelectedEventKey('');
  };

  const handleYearChange = (y) => {
    setYear(y);
    setSelectedEventKey('');
  };

  // ── Widget 1: Fetch badges for selected event ──
  const fetchBadges = useCallback(async () => {
    if (!selectedEventKey || !year) return;
    setBadgesLoading(true);
    try {
      const res = await getCoachRankingBadges(Number(year), selectedEventKey);
      setBadges(res?.items || []);
    } catch (err) {
      console.error('Error fetching badges:', err);
      setBadges([]);
    } finally {
      setBadgesLoading(false);
    }
  }, [selectedEventKey, year]);

  useEffect(() => { fetchBadges(); }, [fetchBadges]);

  // ── Widget 2: Fetch full ranking for comparison ──
  const fetchComparison = useCallback(async () => {
    if (!selectedEventKey || !year) return;
    setComparisonLoading(true);
    try {
      const res = await getVukzRankings({
        year: Number(year),
        event_key: selectedEventKey,
        scope: 'vukz',
        limit: 50,
      });
      setComparisonData(res?.items || []);
    } catch (err) {
      console.error('Error fetching comparison:', err);
      setComparisonData([]);
    } finally {
      setComparisonLoading(false);
    }
  }, [selectedEventKey, year]);

  useEffect(() => { fetchComparison(); }, [fetchComparison]);

  // ── Widget 3: Fetch athlete drill-down ──
  const fetchAthleteSummary = useCallback(async () => {
    if (!selectedAthleteId || !year) return;
    setAthleteSummaryLoading(true);
    try {
      const res = await getAthleteRankingSummary(Number(selectedAthleteId), Number(year));
      setAthleteSummary(res?.items || []);
    } catch (err) {
      console.error('Error fetching athlete summary:', err);
      setAthleteSummary([]);
    } finally {
      setAthleteSummaryLoading(false);
    }
  }, [selectedAthleteId, year]);

  useEffect(() => { fetchAthleteSummary(); }, [fetchAthleteSummary]);

  // ── Helpers ──
  const myAthleteIds = new Set(athletes.map((a) => a.athlete_user_id || a.id));
  const isMyAthlete = (id) => myAthleteIds.has(id);

  // ── Render ──
  return (
    <div className="min-h-screen" style={{ backgroundColor: VUKZ.bg }}>
      <div className="max-w-6xl mx-auto px-4 lg:px-6 py-6">

        {/* ═══ Page Header ═══ */}
        <PageHeader
          title={t.title}
          subtitle={t.subtitle(athletes.length)}
          badgeText={t.badge}
          rightSlot={
            <div className="flex items-center gap-2">
              <GenderToggle value={gender} onChange={handleGenderChange} />
              <SelectPill
                value={year}
                onChange={handleYearChange}
                icon={Calendar}
                options={AVAILABLE_YEARS.map((y) => ({ value: String(y), label: String(y) }))}
              />
            </div>
          }
          className="mb-6"
        />

        <div className="space-y-6">

          {/* ═══ Widget 1: Meus Atletas na Prova ═══ */}
          <WidgetCard
            icon={Trophy}
            title={t.w1title}
            subtitle={selectedEventKey
              ? t.w1sub(parseEventLabel(selectedEventKey), year)
              : t.w1subEmpty}
            filters={
              <SelectPill
                value={selectedEventKey}
                onChange={setSelectedEventKey}
                icon={Filter}
                placeholder={eventsLoading ? t.loading : t.eventPh}
                options={filteredEvents.map((e) => ({
                  value: buildEventKey(e.distance_meters, e.normalizedStroke, gender),
                  label: e.displayName,
                }))}
              />
            }
          >
            {badgesLoading || athletesLoading ? (
              <SkeletonRows count={4} />
            ) : !selectedEventKey ? (
              <EmptyState icon={Trophy} message={t.w1empty1} />
            ) : badges.length === 0 ? (
              <EmptyState icon={Users} message={t.w1empty2} />
            ) : (
              <div className="overflow-x-auto -mx-5">
                <table className="w-full min-w-[500px]">
                  <thead>
                    <tr className="border-b" style={{ borderColor: VUKZ.border }}>
                      {t.cols1.map((h) => (
                        <th key={h} className="text-left px-5 py-2.5 text-[10px] font-bold uppercase tracking-wider"
                          style={{ color: VUKZ.textLight }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {badges.map((b, idx) => (
                      <tr key={b.athlete_id || idx}
                        className="border-b hover:bg-gray-50/50 transition-colors cursor-pointer"
                        style={{ borderColor: VUKZ.border }}
                        onClick={() => setSelectedAthleteId(String(b.athlete_id))}
                      >
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: VUKZ.primaryLight }}>
                              <User size={12} style={{ color: VUKZ.primary }} />
                            </div>
                            <span className="text-xs font-bold" style={{ color: VUKZ.text }}>
                              {b.athlete_name}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <span className="text-xs font-extrabold font-mono" style={{ color: VUKZ.primary }}>
                            {b.best_value || '—'}
                          </span>
                        </td>
                        <td className="px-5 py-3"><RankBadge rank={b.rank_vukz} scope="vukz" /></td>
                        <td className="px-5 py-3"><RankBadge rank={b.rank_br} scope="br" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </WidgetCard>

          {/* ═══ Widget 2: Meus Atletas vs Ranking VUKZ ═══ */}
          <WidgetCard
            icon={BarChart3}
            title={t.w2title}
            subtitle={selectedEventKey
              ? t.w2sub(parseEventLabel(selectedEventKey), year)
              : t.w2subEmpty}
          >
            {comparisonLoading ? (
              <SkeletonRows count={8} />
            ) : !selectedEventKey ? (
              <EmptyState icon={Target} message={t.w2empty1} />
            ) : comparisonData.length === 0 ? (
              <EmptyState icon={Target} message={t.w2empty2} />
            ) : (
              <div className="overflow-x-auto -mx-5">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="border-b" style={{ borderColor: VUKZ.border }}>
                      {t.cols2.map((h) => (
                        <th key={h} className="text-left px-5 py-2.5 text-[10px] font-bold uppercase tracking-wider"
                          style={{ color: VUKZ.textLight }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.map((r, idx) => {
                      const mine = isMyAthlete(r.athlete_id);
                      return (
                        <tr key={`${r.athlete_id}-${idx}`}
                          className={`border-b transition-colors ${mine ? 'bg-gray-50' : 'hover:bg-gray-50/50'}`}
                          style={{
                            borderColor: VUKZ.border,
                            ...(mine && { borderLeft: `3px solid ${VUKZ.primary}` }),
                          }}
                        >
                          <td className="px-5 py-3">
                            {r.rank <= 3 ? (
                              <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                                style={{
                                  backgroundColor: `${['#FFD700', '#C0C0C0', '#CD7F32'][r.rank - 1]}20`,
                                  color: ['#FFD700', '#C0C0C0', '#CD7F32'][r.rank - 1],
                                }}>
                                <Medal size={14} />
                              </div>
                            ) : (
                              <span className="text-xs font-extrabold" style={{ color: VUKZ.textLight }}>
                                {r.rank}
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                                style={{ backgroundColor: mine ? VUKZ.primary : VUKZ.primaryLight }}>
                                <User size={12} style={{ color: mine ? 'white' : VUKZ.primary }} />
                              </div>
                              <div>
                                <span className={`text-xs font-bold ${mine ? 'underline' : ''}`}
                                  style={{ color: VUKZ.text }}>
                                  {r.athlete_name}
                                </span>
                                {mine && (
                                  <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded-md"
                                    style={{ backgroundColor: `${VUKZ.primary}15`, color: VUKZ.primary }}>
                                    {t.myBadge}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3">
                            <span className="text-[10px]" style={{ color: VUKZ.textLight }}>
                              {r.country || '—'}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            <span className="text-[10px]" style={{ color: VUKZ.textLight }}>
                              {r.category_name || '—'}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            <span className="text-xs font-extrabold font-mono"
                              style={{ color: mine ? VUKZ.primary : VUKZ.text }}>
                              {r.result_value || '—'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </WidgetCard>

          {/* ═══ Widget 3: Visão por Atleta ═══ */}
          <WidgetCard
            icon={Award}
            title={t.w3title}
            subtitle={selectedAthleteId
              ? t.w3sub(year)
              : t.w3subEmpty}
            filters={
              <SelectPill
                value={selectedAthleteId}
                onChange={setSelectedAthleteId}
                icon={User}
                placeholder={athletesLoading ? t.loading : t.athletePh}
                options={athletes.map((a) => ({
                  value: String(a.athlete_user_id || a.id),
                  label: a.athlete_name || a.name || `Atleta #${a.athlete_user_id || a.id}`,
                }))}
              />
            }
          >
            {athleteSummaryLoading ? (
              <SkeletonRows count={5} />
            ) : !selectedAthleteId ? (
              <EmptyState icon={User} message={t.w3empty1} />
            ) : athleteSummary.length === 0 ? (
              <EmptyState icon={Award} message={t.w3empty2(year)} />
            ) : (
              <div className="overflow-x-auto -mx-5">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="border-b" style={{ borderColor: VUKZ.border }}>
                      {t.cols3.map((h) => (
                        <th key={h} className="text-left px-5 py-2.5 text-[10px] font-bold uppercase tracking-wider"
                          style={{ color: VUKZ.textLight }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {athleteSummary.map((item) => (
                      <tr key={item.event_key}
                        className="border-b hover:bg-gray-50/50 transition-colors"
                        style={{ borderColor: VUKZ.border }}
                      >
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <Waves size={14} style={{ color: VUKZ.primary }} />
                            <span className="text-xs font-bold" style={{ color: VUKZ.text }}>
                              {item.event_label || parseEventLabel(item.event_key)}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <span className="text-xs font-extrabold font-mono" style={{ color: VUKZ.primary }}>
                            {item.best_value || '—'}
                          </span>
                        </td>
                        <td className="px-5 py-3"><RankBadge rank={item.rank_vukz} scope="vukz" /></td>
                        <td className="px-5 py-3"><RankBadge rank={item.rank_br} scope="br" /></td>
                        <td className="px-5 py-3"><RankBadge rank={item.rank_usa} scope="usa" /></td>
                        <td className="px-5 py-3"><RankBadge rank={item.rank_aus} scope="aus" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </WidgetCard>

        </div>
      </div>
    </div>
  );
};

export default CoachPerformancePage;
